import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { useSdk } from '~/sdk';
import type { GetPudosResponse, Pudo, UpdateCheckoutCarrierRequest } from '@vue-storefront/prestashop-api';
import type { Reactive } from 'vue';
import { cloneDeep } from 'lodash-es';

export const usePudos = () => {
  const queryClient = useQueryClient();
  const filters = useState('pudos-filters', () => ({ from: 0, to: 10 }));
  const { currentCarrier } = useCarriers();

  const query = useQuery({
    queryKey: ['pudos'],
    queryFn: async (): Promise<GetPudosResponse> => {
      console.log('to:', filters.value.to)
      return await useSdk().commerce.getCollectionPoints({ from: filters.value.from, to: filters.value.to });
    },
  });

  // Update carrier mutation
  const mutation = useMutation({
    mutationFn: async (data: UpdateCheckoutCarrierRequest) => {
      return await useSdk().commerce.updateCheckoutCarrier({
        ...data,
      });
    },
    onMutate: async (newPudo) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ['pudos'] });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(['pudos']);

      const newData = cloneDeep({ ...unref(query.data.value) });
      newData.content = newData.content || { pudoList: [] as Pudo[], selectedPudo: null };
      newData.content['selectedPudo'] = newData.content.pudoList.find(pudo => pudo.ID === newPudo.selected_pudo) ?? null;
      // Optimistically update to the new value
      queryClient.setQueryData(['pudos'], newData);

      // Return a context with the previous and new todo
      return { previousData, newData };
    },
    // If the mutation fails, use the context we returned above
    onError: (err, newTodo, context) => {
      queryClient.setQueryData(['pudos'], context?.previousData);
    },
    // Always refetch after error or success:
    onSettled: (newTodo) => {
      queryClient.invalidateQueries({ queryKey: ['pudos'] });
      queryClient.invalidateQueries({ queryKey: ['cart'] });
    }
  });

  return {
    filters: { ...toRefs(filters.value) },
    query: reactive(query) satisfies Reactive<typeof query>,
    mutation: reactive(mutation) satisfies Reactive<typeof mutation>,
    isActive: computed(() => currentCarrier.value?.id_reference == 545),
    currentPudo: computed(() => query.data?.value?.content?.selectedPudo ?? null),
  };
};
